import { Alert, Button, CircularProgress, Paper, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import useApiHook from "../hooks/useApiHook";
import { useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';
import useAppView from "./platforms/useAppView";
import { useNotification } from "../store/notification-context";

const Transcription = ({ transcription, onAnalyzeWithAI = null, onSummaryGenerated = null }) => {
    const { response, makeRequest, headers } = useApiHook();
    const { makeRequest: generateSummaryRequest, loading: isGeneratingSummary } = useApiHook();
    const { fileId } = useParams();
    const { isNativePlatform } = useAppView();
    const { pushNotification } = useNotification();
    const summaryRef = useRef(null);
    const summaryRequestedRef = useRef(false);
    const [summaryError, setSummaryError] = useState('');
    const [generatedSummary, setGeneratedSummary] = useState(null);
    const visibleTranscription = generatedSummary || transcription;
    const hasSummaryTokenUsage = Number.isInteger(visibleTranscription.summaryInputTokens)
        && Number.isInteger(visibleTranscription.summaryOutputTokens);
    const formatTokenCount = (value) => new Intl.NumberFormat('it-IT').format(value);
    const transcriptionObj = JSON.parse(transcription.transcription);
    const sentences = transcriptionObj.map(sentence => {
        return <Typography key={sentence.start} flexWrap={"wrap"} my={2} variant='body1'>{sentence.text}</Typography>;
    })
    const secondsTaken = Math.floor(transcription.timeTaken / 1000);

    const downloadTranscriptionClickHandler = (ext) => {
        makeRequest(`/file/${fileId}/tr_${transcription.id}/${ext}`, 'get', {}, { responseType: "blob" });
    }

    const scrollToSummary = () => {
        summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const summaryClickHandler = async () => {
        if (visibleTranscription.summary) {
            scrollToSummary();
            return;
        }

        setSummaryError('');
        summaryRequestedRef.current = true;
        const result = await generateSummaryRequest(`/file/${fileId}/transcriptions/${transcription.id}/summary`, 'post');
        if (result?.state === 'success' && result.data?.transcription) {
            setGeneratedSummary(result.data.transcription);
            onSummaryGenerated?.(result.data.transcription);
            pushNotification({
                title: 'Sintesi generata',
                message: 'La sintesi è stata generata con successo.'
            });
            return;
        }
        summaryRequestedRef.current = false;
        setSummaryError(result?.message || 'Impossibile generare la sintesi. Riprova tra poco.');
    };

    useEffect(() => {
        if (!summaryRequestedRef.current || !visibleTranscription.summary) {
            return;
        }

        window.requestAnimationFrame(scrollToSummary);
        summaryRequestedRef.current = false;
    }, [visibleTranscription.summary]);

    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result
                    .replace('data:', '')
                    .replace(/^.+,/, '');
                resolve(base64String);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    useEffect(() => {
        if (response) {
            const fileName = `${headers['x-file-name']}.${headers['x-file-ext']}`;

            if (isNativePlatform) {
                blobToBase64(response).then(async (base64Data) => {
                    try {
                        // Salva nella cache privata dell'app (non richiede permessi)
                        const writeResult = await Filesystem.writeFile({
                            path: fileName,
                            data: base64Data,
                            directory: Directory.Cache,
                        });
                        // Apri il file con il visualizzatore di sistema
                        const mimeMap = {
                            'pdf': 'application/pdf',
                            'txt': 'text/plain',
                            'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        };
                        const ext = fileName.split('.').pop();
                        const mimeType = mimeMap[ext] || 'application/octet-stream';
                        await FileOpener.openFile({
                            path: writeResult.uri,
                            mimeType,
                        });
                    } catch (e) {
                        console.error("Errore apertura file", e);
                        alert("Errore durante il download o l'apertura del file.");
                    }
                });
            } else {
                const url = window.URL.createObjectURL(response);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute(
                    'download',
                    fileName,
                );

                // Append to html link element page
                document.body.appendChild(link);

                // Start download
                link.click();

                // Clean up and remove the link
                link.parentNode.removeChild(link);
            }
        }
    }, [headers, isNativePlatform, response]);

    return <Stack direction="column" spacing={1} flexGrow={1}>
        <Paper sx={{ p: 2 }} elevation={2}>
            <Stack mt={1} direction='row' spacing={1}>
                <Button variant="outlined" color="error" startIcon={<PictureAsPdfIcon />} onClick={downloadTranscriptionClickHandler.bind(this, "pdf")}>
                    PDF
                </Button>
                <Button variant="outlined" color="error" startIcon={<FormatAlignLeftIcon />} onClick={downloadTranscriptionClickHandler.bind(this, "txt")}>
                    TXT
                </Button>
                <Button variant="outlined" color="error" startIcon={<DescriptionIcon />} onClick={downloadTranscriptionClickHandler.bind(this, "doc")}>
                    DOC
                </Button>
                <Button
                    variant="contained"
                    startIcon={isGeneratingSummary || visibleTranscription.summaryStatus === 'processing' ? <CircularProgress size={16} color="inherit" /> : <SummarizeOutlinedIcon />}
                    onClick={summaryClickHandler}
                    disabled={isGeneratingSummary || visibleTranscription.summaryStatus === 'processing'}
                >
                    {isGeneratingSummary || visibleTranscription.summaryStatus === 'processing' ? 'Generazione sintesi in corso' : 'Sintesi'}
                </Button>
                {onAnalyzeWithAI && (
                    <Button variant="outlined" startIcon={<AutoAwesomeOutlinedIcon />} onClick={onAnalyzeWithAI}>
                        Analizza AI
                    </Button>
                )}
            </Stack>
            <Typography mt={1} variant="body1" fontWeight="normal">
                Tempo Trascrizione: {secondsTaken > 59 && `${Math.floor(secondsTaken / 60)} min `}
                {secondsTaken % 60} sec
            </Typography>
        </Paper>
        <Paper elevetion={2}>
            <Box p={2}>
                {sentences}
            </Box>
        </Paper>
        {summaryError && <Alert severity="error">{summaryError}</Alert>}
        {visibleTranscription.summary && (
            <Paper ref={summaryRef} elevation={2}>
                <Box p={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 1 }} alignItems={{ sm: 'baseline' }} mb={1}>
                        <Typography variant="h6">Sintesi</Typography>
                        {hasSummaryTokenUsage && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Totale: {formatTokenCount(visibleTranscription.summaryInputTokens + visibleTranscription.summaryOutputTokens)} token</strong>
                                {' '}({formatTokenCount(visibleTranscription.summaryInputTokens)} input - {formatTokenCount(visibleTranscription.summaryOutputTokens)} output)
                            </Typography>
                        )}
                    </Stack>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {visibleTranscription.summary}
                    </Typography>
                </Box>
            </Paper>
        )}
    </Stack>
}

export default Transcription;
